import { RightOutlined } from '@ant-design/icons'
import { ApiError } from '@community_dev/requests'
import { COMMUNITY_REGISTRATION_STATUS, CommunityRegistrationStatus } from '@community_dev/types/lib/api/v2/TCR'
import { Skeleton } from 'antd'
import type { JSX, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { BORDER, MUTED_TEXT, SURFACE, TEXT, Tone, TONES, TRACK_BG } from '../../colors'
import { PLAN_TYPES } from '../Billing/components/Account/constants'
import { isThrottlingActive } from '../Billing/components/Subscription/messageLimit'
import { StatusDot } from '../StatusBadge'

import { ACCOUNT_STATUS_DISPLAY, formatTcrStatus, pluralize } from './health'
import { ClientHealth } from './useClientHealth'

import { Client } from 'api/clients'
import { ROUTES } from 'constants/routes'
import { route } from 'utils/router'

const StyledTiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const StyledTile = styled(Link)`
  display: flex;
  flex: 1 1 190px;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 18px 20px;
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: 8px;
  color: ${TEXT};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    border-color: ${MUTED_TEXT};
    color: ${TEXT};
  }
`

const StyledTileLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${MUTED_TEXT};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const StyledTileValue = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 700;

  small {
    color: ${MUTED_TEXT};
    font-size: 14px;
    font-weight: 500;
  }
`

const StyledTileDetail = styled.span<{ $tone?: Tone }>`
  color: ${({ $tone }) => ($tone ? TONES[$tone].fg : MUTED_TEXT)};
  font-size: 13px;
  font-weight: ${({ $tone }) => ($tone ? 600 : 400)};
`

const StyledTrack = styled.span`
  display: block;
  height: 6px;
  border-radius: 3px;
  background: ${TRACK_BG};
`

const StyledTrackFill = styled.span<{ $percent: number; $tone: Tone }>`
  display: block;
  width: ${({ $percent }) => $percent}%;
  height: 6px;
  border-radius: 3px;
  background: ${({ $tone }) => ($tone === 'neutral' ? TEXT : TONES[$tone].dot)};
`

const TCR_STATUS_TONES: Record<CommunityRegistrationStatus, Tone> = {
  [COMMUNITY_REGISTRATION_STATUS.APPROVED]: 'success',
  [COMMUNITY_REGISTRATION_STATUS.EXEMPT]: 'success',
  [COMMUNITY_REGISTRATION_STATUS.PENDING]: 'warning',
  [COMMUNITY_REGISTRATION_STATUS.NOT_SET]: 'neutral',
  [COMMUNITY_REGISTRATION_STATUS.REJECTED]: 'error',
  [COMMUNITY_REGISTRATION_STATUS.REVOKED]: 'error',
}

const TILE_SKELETON = <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />

function Tile({ label, to, children }: { label: string; to: string; children: ReactNode }): JSX.Element {
  return (
    <StyledTile to={to}>
      <StyledTileLabel>
        {label}
        <RightOutlined aria-hidden="true" />
      </StyledTileLabel>
      {children}
    </StyledTile>
  )
}

function AccountTile({ client, health }: { client: Client; health: ClientHealth }): JSX.Element {
  const { data, isInitialLoading, isError, error } = health.billingClient
  const status = data?.accountStatus ? ACCOUNT_STATUS_DISPLAY[data.accountStatus] : undefined
  const planType = PLAN_TYPES.find(({ value }) => value === data?.planType)?.label

  function renderContent() {
    if (isInitialLoading) return TILE_SKELETON
    if (isError) {
      return (
        <StyledTileValue>
          {(error as ApiError | null)?.status === 404 ? 'No billing account' : 'Could not load'}
        </StyledTileValue>
      )
    }
    return (
      <>
        <StyledTileValue>
          <StatusDot $tone={status?.tone ?? 'neutral'} aria-hidden="true" />
          {status?.label ?? 'No status'}
        </StyledTileValue>
        <StyledTileDetail>{planType ?? 'No plan type'}</StyledTileDetail>
      </>
    )
  }

  return (
    <Tile label="Account" to={route(ROUTES.CLIENT.BILLING, { id: client.id })}>
      {renderContent()}
    </Tile>
  )
}

function MessagesTile({ client }: { client: Client }): JSX.Element {
  const used = client.campaignMessagesMonthlyUsed
  const threshold = client.campaignMessagesMonthlyThreshold
  const hasLimit = isThrottlingActive(threshold)
  const percent = hasLimit ? Math.min(100, Math.round((used / threshold) * 100)) : 0
  const tone: Tone = percent >= 100 ? 'error' : percent >= 90 ? 'warning' : 'neutral'

  return (
    <Tile label="Messages this month" to={route(ROUTES.CLIENT.BILLING, { id: client.id })}>
      <StyledTileValue>
        {used.toLocaleString('en-US')}
        {hasLimit && <small>/ {threshold.toLocaleString('en-US')}</small>}
      </StyledTileValue>
      {hasLimit ? (
        <StyledTrack
          aria-label={`${percent}% of the monthly message limit used`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={percent}
          role="progressbar"
        >
          <StyledTrackFill $percent={percent} $tone={tone} />
        </StyledTrack>
      ) : (
        <StyledTileDetail>No monthly limit</StyledTileDetail>
      )}
    </Tile>
  )
}

function PhoneNumbersTile({ client, health }: { client: Client; health: ClientHealth }): JSX.Element {
  const { data, isInitialLoading, isError, error } = health.provisioning
  const overview = data?.overview

  function renderContent() {
    if (isInitialLoading) return TILE_SKELETON
    if (isError && (error as ApiError | null)?.status !== 404) return <StyledTileValue>Could not load</StyledTileValue>
    if (!overview) return <StyledTileValue>No provisioning data</StyledTileValue>
    return (
      <>
        <StyledTileValue>{overview.readyNumbers.toLocaleString('en-US')} ready</StyledTileValue>
        <span>
          <StyledTileDetail $tone={overview.errorNumbers > 0 ? 'error' : undefined}>
            {pluralize(overview.errorNumbers, 'error')}
          </StyledTileDetail>
          <StyledTileDetail> · {overview.pendingNumbers.toLocaleString('en-US')} pending</StyledTileDetail>
        </span>
      </>
    )
  }

  return (
    <Tile label="Phone numbers" to={route(ROUTES.CLIENT.NUMBER_PROVISIONING, { id: client.id })}>
      {renderContent()}
    </Tile>
  )
}

function TcrTile({ client, health }: { client: Client; health: ClientHealth }): JSX.Element {
  const { data, isInitialLoading, isError } = health.tcr
  const registration = data?.data

  function renderContent() {
    if (isInitialLoading) return TILE_SKELETON
    if (isError) return <StyledTileValue>Could not load</StyledTileValue>
    if (!registration) return <StyledTileValue>No registration data</StyledTileValue>
    return (
      <>
        <StyledTileValue>
          <StatusDot $tone={TCR_STATUS_TONES[registration.status] ?? 'neutral'} aria-hidden="true" />
          {formatTcrStatus(registration.status)}
        </StyledTileValue>
        <StyledTileDetail>SMS status: {registration.client.calculatedSmsStatus ?? '-'}</StyledTileDetail>
      </>
    )
  }

  return (
    <Tile label="TCR" to={route(ROUTES.CLIENT.TCR, { id: client.id })}>
      {renderContent()}
    </Tile>
  )
}

export function HealthTiles({ client, health }: { client: Client; health: ClientHealth }): JSX.Element {
  return (
    <StyledTiles>
      <AccountTile client={client} health={health} />
      <MessagesTile client={client} />
      {health.isNotTwilio && <PhoneNumbersTile client={client} health={health} />}
      {health.isCommunityCSPClient && <TcrTile client={client} health={health} />}
    </StyledTiles>
  )
}
